import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Container from "../components/common/Container";
import FundesciLogo from "../assets/img/common/fundesci-logo.svg";
import { ReactComponent as AttachIcon } from "../assets/img/common/attach-file-icon.svg";

import { UN_REGISTERED } from "../Constants";

import {
  useAddResultCommentMutation,
  useDeleteResultCommentMutation,
  useDislikeResultMutation,
  useEditResultCommentMutation,
  useGetResultByIdQuery,
  useLazyGetResultByIdQuery,
  useLikeResultMutation,
} from "../redux/slices/api/resultApiSlice";
import { Bars, Oval } from "react-loader-spinner";
import useNavigateToProfile from "../hooks/useNavigateToProfile";
import useCheckProjectPermissions from "../hooks/useCheckProjectPermissions";
import SharePopup from "../components/common/SharePopuop";
import LikeButton from "../components/common/LikeButton";
import { BsShareFill } from "react-icons/bs";
import { useGetUserQuery } from "../redux/slices/api/userApi";
import {
  selectGeneral,
  setShowAuthModal,
  setShowFullscreenImage,
} from "../redux/slices/generalSlice";
import { useDispatch, useSelector } from "react-redux";
import { handleError } from "../hooks/useApi";
import notify from "../components/common/notify";
import UserPlaceHolderImg from "../assets/img/common/user-placeholder-img.png";
import CommentItem from "../components/common/CommentItem";
import { FaCrown, FaRegComment } from "react-icons/fa";
import { scroller } from "react-scroll";
import { GoLock } from "react-icons/go";
import ExternalLink from "../components/common/ExternalLink";
import MetaTags from "../components/common/MetaTags";
import FullScreenImage from "../components/common/FullscreenImage";

const UserSubtitle = ({ creator }) => {
  const { title_rank, role, university_college, location, email } =
    creator ?? {};
  if (role === "applicant") {
    if (university_college) {
      return (
        <p className="text-sm text-custom-gray-700">
          <span className="font-medium text-custom-gray-900">{title_rank}</span>{" "}
          at{" "}
          <span className="font-medium text-custom-gray-900">
            {university_college}
          </span>
        </p>
      );
    }
    return (
      <span className="font-medium text-custom-gray-800">{title_rank}</span>
    );
  } else {
    return (
      <span className="font-medium text-custom-gray-800">
        {location || email}
      </span>
    );
  }
};

const ResultDetail = () => {
  // State to manage the visibility of the share popup
  const [showSharePopup, setShowSharePopup] = useState(false);

  // Extracting the 'id' parameter from the route using React Router's 'useParams' hook
  let { id: result_id_params } = useParams();

  // Custom hook to navigate to user profiles
  const { navigateToProfile } = useNavigateToProfile();

  // Query to fetch result data by ID
  const { isLoading: resultDataLoading, data } = useGetResultByIdQuery(
    { id: result_id_params },
    {
      skip: !result_id_params,
    }
  );

  const dispatch = useDispatch();

  // Destructuring data from the query result
  const { result: resultData, comments, has_access } = data ?? {};
  const {
    project,
    feature_image,
    title,
    attachment,
    links,
    result,
    writers,
    likes,
    premium,
    keywords,
    types: projectTypes,
  } = resultData ?? {};

  // Custom hook to check user permissions for accessing project results
  const { hasAccessToResult } = useCheckProjectPermissions({
    projectData: project,
  });

  // Memoized array of result writers excluding the project creator
  const resultWriters = useMemo(
    () => writers?.filter((item) => item?._id !== project?.creator?._id),
    [writers, project]
  );

  // React Router's navigate function
  const navigate = useNavigate();

  // State to manage the content of the comment
  const [bodyContent, setBodyContent] = useState("");

  // Lazy query to refetch result data when triggered
  const [triggerGetResultData] = useLazyGetResultByIdQuery();

  // Mutation to add a comment to the result
  const [addComment, { isLoading: addCommentLoading }] =
    useAddResultCommentMutation();

  // Redux selector to check if the user is logged in
  const { isLoggedIn, showFullscreenImage } = useSelector(selectGeneral);

  // Query to fetch the current user's data
  const {
    isLoading: getMeLoading,
    data: meData,
    refetch: refetchUserData,
  } = useGetUserQuery(null, { skip: !isLoggedIn });

  // Extracting user data from the query result
  const { user: userData } = meData ?? {};

  // Function to handle the submission of a comment
  const handleSubmitComment = async () => {
    // Just need to be logged in - no SIWE signature required for comments
    if (!isLoggedIn) {
      dispatch(setShowAuthModal({ show: true }));
      return;
    }
    
    try {
      // Adding a comment through the mutation
      const res = await addComment({
        id: result_id_params,
        body: {
          body: bodyContent,
        },
      }).unwrap();

      // Resetting the comment body content and triggering a refetch of result data
      setBodyContent("");
      await triggerGetResultData({ id: result_id_params }).unwrap();

      // Displaying a success notification
      notify(res?.message, "success");
    } catch (error) {
      // Handling and displaying an error notification
      handleError(error, null, true);
    }
  };

  if (resultDataLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center fixed bg-custom-gray-100 top-0 left-0 z-50">
        <Bars
          height="50"
          width="50"
          color="#8538ff"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
        />
      </div>
    );
  } else
    return (
      <div className="w-full flex flex-col items-center pt-28 ">
        <MetaTags
          title={title}
          description={`NFT- Fundraising By FunDeSci An innovative approach for decentralized crowdfunding of scientific projects`}
          link={`/results/${result_id_params}`}
          imageCard={feature_image}
        />
        <Container className="flex flex-col items-center ">
          <div className="w-full flex flex-col sm:flex-row justify-between py-3 items-start sm:items-end gap-3">
            <div>
              <h3 className="text-3xl font-bold abel-font bg-gradient-to-r from-[#8538ff] to-[#7e7cff] bg-clip-text text-transparent">
                Research Result
              </h3>
              <p className="text-sm text-gray-500 mt-1">Detailed findings and analysis</p>
            </div>
            {hasAccessToResult && (
              <Link
                to={`/create-result?project-id=${project?._id}&result-id=${result_id_params}`}
                className="px-6 h-10 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] hover:from-[#7e7cff] hover:to-[#8538ff] flex justify-center items-center text-white text-sm rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Edit Result
              </Link>
            )}{" "}
          </div>
          <div className="w-full flex flex-col mt-6 pb-20">
            <section className="w-full flex flex-col mlg:flex-row gap-6 mlg:h-[520px]">
              {/* result img */}
              <div className="w-full mlg:w-[55%] lg:w-[50%] h-full">
                <div className="w-full mlg:w-11/12 relative h-full rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                  <img
                    onClick={() => dispatch(setShowFullscreenImage(true))}
                    src={feature_image || FundesciLogo}
                    alt="result-img"
                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute right-4 top-4 rounded-2xl bg-white/90 backdrop-blur-sm text-sm px-3 pb-5 pt-4 flex flex-col gap-4 items-center shadow-lg">
                    <LikeButton
                      likes={likes}
                      useLikeMutation={useLikeResultMutation}
                      useDislikeMutation={useDislikeResultMutation}
                      id={result_id_params}
                      likeType="result"
                      containerClass="w-8 h-9"
                      heartClass="w-16 h-16"
                      countClass="text-xs -mt-5"
                    />
                    <button
                      onClick={() => {
                        scroller.scrollTo(
                          has_access ? "comments" : "premium-lock",
                          {
                            duration: 1000,
                            smooth: "easeInOutQuint",
                            offset: -130,
                          }
                        );
                      }}
                      className="flex flex-col items-center hover:text-primary transition-colors duration-300"
                    >
                      <FaRegComment
                        size={20}
                        className="text-gray-700"
                      />
                      <span className="text-sm mt-1 font-medium">
                        {comments?.length || 0}
                      </span>
                    </button>

                    <button onClick={() => setShowSharePopup(true)} className="hover:scale-110 transition-transform duration-300">
                      <BsShareFill size={16} className="text-[#3C2166]" />
                    </button>
                  </div>
                </div>
              </div>
              {/* result detail */}
              <div className="w-full mlg:w-[45%] lg:w-[50%] flex flex-col h-full">
                <h1 className="text-3xl xl:text-4xl font-bold flex items-start leading-tight">
                  {premium && (
                    <FaCrown className="text-yellow-500 text-3xl xl:text-4xl mr-2.5 pt-1 xl:pt-0.5 min-w-fit" />
                  )}{" "}
                  {title}
                </h1>
                <span className="text-sm text-gray-500 mt-2 font-medium">
                  {projectTypes.join(" · ")}
                </span>
                <div className="flex items-center flex-wrap gap-2 mt-5">
                  {keywords?.length
                    ? keywords?.slice(0, 3)?.map((item, index) => (
                        <Link
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          to={`/discovery?keywords=${item}`}
                          key={index}
                          className="py-2 px-4 rounded-full bg-gradient-to-r from-purple-50 to-blue-50 text-primary text-sm font-medium hover:from-purple-100 hover:to-blue-100 transition-all duration-300 shadow-sm hover:shadow"
                        >
                          {item}
                        </Link>
                      ))
                    : null}
                </div>
                {attachment && (
                  <div className="mt-6 p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-sm">
                    <h5 className="font-bold text-gray-800 mb-2">Attachment</h5>
                    <a
                      href={attachment}
                      target="_blank"
                      rel="noreferrer"
                      download
                      className="flex items-center mt-2 cursor-pointer text-primary hover:text-[#7e7cff] transition-colors duration-300"
                    >
                      <AttachIcon />
                      <p className="text-sm ml-2 font-medium">
                        {" "}
                        {attachment.substring(attachment.lastIndexOf("/") + 1)}
                      </p>
                    </a>
                  </div>
                )}
                {/* <h5 className="font-semibold mt-6">Result Summary</h5>
                <p className="text-sm text-custom-gray-600 max-w-[95%] max-h-[120px] lg:max-h-[80px]  whitespace-pre-wrap text-left ">
                  {result_summary} ... &nbsp;
                  <span
                    onClick={() => {
                      scroller.scrollTo(
                        has_access ? "result" : "premium-lock",
                        {
                          duration: 300,
                          smooth: "easeInOutQuint",
                          offset: -160,
                        }
                      );
                    }}
                    className="text-blue-link cursor-pointer"
                  >
                    See more
                  </span>
                </p> */}

                <div className="w-full flex flex-col pt-6 mt-6 border-t-2 border-gray-100">
                  <h5 className="font-bold text-lg text-gray-800 mb-3">Project Info</h5>
                  <div
                    onClick={() => navigate(`/projects/${project?._id}`)}
                    className="w-full flex flex-col cursor-pointer bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl hover:shadow-md transition-all duration-300"
                  >
                    <div className="w-full flex flex-col justify-between pb-3">
                      <div className="w-full flex items-center">
                        <img
                          className="w-16 h-16 rounded-xl shadow-sm"
                          alt="abstract"
                          src={project?.abstract}
                        />
                        <div className="w-full flex flex-col ml-3 max-w-[90%]">
                          <span
                            className={`text-lg lg:text-xl truncate font-bold text-gray-800`}
                          >
                            {project?.title?.length > 70
                              ? project?.title?.slice(0, 70) + "..."
                              : project?.title}
                          </span>
                          <div className="flex items-center text-gray-600 mt-1">
                            <span className="text-sm font-medium">
                              {project?.fundraising_method}
                            </span>
                          </div>{" "}
                        </div>
                      </div>{" "}
                    </div>
                    <h5 className="font-bold text-gray-800 mt-2 mb-2">Creator & Writers</h5>
                    {writers && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full flex flex-col rounded-xl max-h-36 overflow-auto custom-primary-tiny-scroll gap-y-4 mt-2 pr-2"
                      >
                        <div
                          className={`w-full flex justify-between items-start`}
                        >
                          <div
                            onClick={(e) => {
                              e.stopPropagation();

                              navigateToProfile(
                                project?.creator?._id,
                                project?.creator?.role
                              );
                            }}
                            className="flex items-start cursor-pointer hover:opacity-80 transition-opacity duration-300"
                          >
                            <span className="rounded-lg overflow-hidden flex justify-center items-center cursor-pointer h-20 w-20 min-w-[5rem] ring-2 ring-gray-200 hover:ring-primary transition-all duration-300">
                              <img
                                src={project?.creator.images?.profile}
                                alt={"user-img"}
                                className="transition-all duration-300 hover:scale-110 w-full h-full object-cover"
                              />
                            </span>
                            <div className="flex flex-col ml-3 justify-between">
                              <span className="font-bold text-lg md:text-xl text-gray-800">
                                {project?.creator.fundername ||
                                  project?.creator.firstname +
                                    " " +
                                    project?.creator.lastname}
                              </span>
                              <UserSubtitle creator={project.creator} />
                              {/* ---- skills --- */}
                              {project.creator?.skills?.length ? (
                                <div className="hidden sm:flex items-center flex-wrap gap-3 mt-2">
                                  {project.creator?.skills
                                    ?.slice(0, 3)
                                    ?.map((item, index) => (
                                      <span
                                        key={index}
                                        className="py-1 px-2 rounded-3xl bg-slate-200 text-slate-700 text-xs"
                                      >
                                        {item?.text}
                                      </span>
                                    ))}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {writers?.length
                          ? resultWriters?.map((item, index, arr) => (
                              <div
                                key={index}
                                className={`w-full flex justify-between items-center cursor-pointer`}
                              >
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    item?.role !== UN_REGISTERED &&
                                      navigateToProfile(item?._id, item?.role);
                                  }}
                                  className="flex items-center"
                                >
                                  <span className=" rounded-full overflow-hidden flex justify-center items-center cursor-pointer h-9 w-9">
                                    <img
                                      src={item?.images?.profile}
                                      alt={"user-img"}
                                      className="transition-all duration-300 hover:scale-110 w-full h-full"
                                    />
                                  </span>
                                  <span className="flex flex-col ml-2 justify-between">
                                    <span className="font-semibold text-sm ">
                                      {item?.fundername ||
                                        item?.firstname + " " + item?.lastname}
                                    </span>
                                    <span className=" text-[#797979] text-xs truncate ">
                                      {item?.title_rank ||
                                        item?.location ||
                                        item?.email}
                                    </span>
                                  </span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs text-primary font-bold uppercase">
                                    writer
                                  </span>
                                </div>
                              </div>
                            ))
                          : null}
                      </div>
                    )}
                  </div>{" "}
                </div>
              </div>
            </section>

            {links?.length ? (
              <div className="w-full flex flex-col mlg:w-[55%] lg:w-[50%] mt-8">
                <h5 className="font-bold text-lg text-gray-800 mb-3">Result Links</h5>
                <div className="w-full mlg:w-11/12 flex flex-col bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md overflow-hidden">
                  {links?.map((item, index, arr) =>
                    item?.length ? (
                      <ExternalLink
                        key={index}
                        className={`py-4 px-4 hover:bg-white transition-all duration-300 ${
                          index !== arr.length - 1 &&
                          "border-b border-gray-200"
                        } `}
                        href={item}
                      >
                        {item}
                      </ExternalLink>
                    ) : null
                  )}
                </div>
              </div>
            ) : null}

            <div className="w-full mt-16">
              {has_access ? (
                <div id="result" dangerouslySetInnerHTML={{ __html: result }} />
              ) : (
                <div
                  id="premium-lock"
                  className="w-full py-12 flex flex-col justify-center items-center rounded-2xl text-center bg-gradient-to-br from-purple-50 to-blue-50 shadow-md"
                >
                  <div className="w-20 h-20 rounded-full bg-white shadow-md flex items-center justify-center mb-4">
                    <GoLock className="text-primary text-4xl" />
                  </div>
                  <span className="text-gray-700 mt-2 max-w-lg text-lg font-medium px-4">
                    To see this result, you must be the funder of corresponding
                    project and own the project's NFT by supporting it.
                  </span>
                  {project?.state !== "Closed Call" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project?._id}`);
                      }}
                      className="bg-gradient-to-r from-[#8538ff] to-[#7e7cff] hover:from-[#7e7cff] hover:to-[#8538ff] text-white mt-6 px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Acquire Fundraising NFT
                    </button>
                  )}
                </div>
              )}
            </div>
            {has_access && (
              <div
                className="w-full max-w-4xl flex flex-col gap-y-5 mt-20"
                id="comments"
              >
                <h3 className="text-3xl font-bold abel-font bg-gradient-to-r from-[#8538ff] to-[#7e7cff] bg-clip-text text-transparent">
                  Comments
                </h3>
                <div
                  className={`w-full flex justify-between py-4 items-start gap-4 bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 shadow-sm`}
                >
                  <img
                    src={userData?.images?.profile || UserPlaceHolderImg}
                    alt={""}
                    className="h-12 w-12 rounded-full ring-2 ring-gray-200"
                  />
                  <textarea
                    onChange={(e) => setBodyContent(e.target.value)}
                    value={bodyContent}
                    placeholder={`Leave a comment...`}
                    className="w-full border-2 border-gray-200 outline-none h-24 rounded-xl px-4 py-3 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                  ></textarea>
                  <button
                    type="button"
                    onClick={handleSubmitComment}
                    disabled={addCommentLoading}
                    className="bg-gradient-to-r from-[#8538ff] to-[#7e7cff] hover:from-[#7e7cff] hover:to-[#8538ff] transition-all duration-300 text-white flex items-center justify-center min-w-[6rem] rounded-xl h-10 text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {addCommentLoading ? (
                      <Oval
                        height={23}
                        width={23}
                        color="#ffffff"
                        wrapperStyle={{}}
                        wrapperClass=""
                        visible={true}
                        ariaLabel="oval-loading1"
                        secondaryColor="#446CB3"
                        strokeWidth={2}
                        strokeWidthSecondary={2}
                      />
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
                {comments?.length
                  ? comments?.map((item, index, arr) => (
                      <div
                        key={index}
                        className="flex flex-col items-center w-full "
                      >
                        <CommentItem
                          data={item}
                          key={"comment" + index}
                          useDeleteCommentMutation={
                            useDeleteResultCommentMutation
                          }
                          useAddCommentMutation={useAddResultCommentMutation}
                          useEditCommentMutation={useEditResultCommentMutation}
                          refetch={triggerGetResultData}
                        />
                      </div>
                    ))
                  : null}{" "}
              </div>
            )}
          </div>
        </Container>{" "}
        {showSharePopup && (
          <SharePopup
            title={resultData?.title}
            summary={resultData?.result_summary}
            setShow={setShowSharePopup}
            show={showSharePopup}
            shareType="result"
          />
        )}
        {showFullscreenImage && (
          <FullScreenImage img={feature_image || FundesciLogo} />
        )}
      </div>
    );
};

export default ResultDetail;
